// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext } from '../app';
import { el } from '../dom';
import { formatMass, formatYear } from '../format';
import { stateColor, heatColor } from '../colors';
import { perCapitaByState, groupStateYear } from '../analysis';
import { STATE_LABELS, STATE_ORDER } from '../data';

export function renderStates(root: HTMLElement, ctx: AppContext) {
  root.appendChild(el('h1', { class: 'section-title' }, 'State comparison'));
  root.appendChild(el('p', { class: 'section-sub' }, [
    'Where the emissions actually come from — by raw total and on a ',
    el('span', { class: 'glossary-link', 'data-term': 'per_capita', tabindex: '0' }, 'per-capita'),
    ' basis (using ABS 2024 ERP figures).'
  ]));

  const totals = ctx.data.meta.state_totals;
  const totalsArr = STATE_ORDER.map(s => ({ state: s, ...(totals[s] || { air: 0, water: 0, land: 0, total: 0 }) }));
  totalsArr.sort((a, b) => b.total - a.total);
  const maxTotal = totalsArr[0]?.total ?? 1;

  // Side-by-side: raw + per-capita
  const grid = el('div', { class: 'grid-2 mb-lg' });

  const raw = el('div', { class: 'card' });
  raw.appendChild(el('h2', { class: 'card-title' }, 'Lifetime total — raw kg'));
  raw.appendChild(el('p', { class: 'card-subtitle' }, 'Air + water + land, 1998–2025.'));
  raw.appendChild(el('div', { style: 'display: flex; flex-direction: column; gap: var(--space-sm);' },
    totalsArr.map(s => el('div', null, [
      el('div', { style: 'display: flex; justify-content: space-between; font-size: var(--font-size-sm); margin-bottom: 4px' }, [
        el('span', null, STATE_LABELS[s.state] ?? s.state),
        el('span', { class: 'text-mono text-secondary' }, formatMass(s.total))
      ]),
      el('div', { style: 'background: var(--bg-elevated); border-radius: 6px; height: 14px; overflow: hidden; display:flex' }, [
        seg(s.air / s.total, 'var(--air)'),
        seg(s.water / s.total, 'var(--water)'),
        seg(s.land / s.total, 'var(--land)')
      ]),
      el('div', { class: 'text-tertiary', style: 'font-size: var(--font-size-xs); margin-top: 4px; display:flex; gap:var(--space-md); flex-wrap:wrap' }, [
        el('span', null, [el('span', { class: 'pill pill-medium-air' }, 'Air'), ' ', formatMass(s.air)]),
        el('span', null, [el('span', { class: 'pill pill-medium-water' }, 'Water'), ' ', formatMass(s.water)]),
        el('span', null, [el('span', { class: 'pill pill-medium-land' }, 'Land'), ' ', formatMass(s.land)])
      ]),
      el('div', { style: 'background: var(--bg-elevated); border-radius: 4px; height: 4px; overflow: hidden; margin-top: 4px;' }, [
        el('div', { style: `background: ${stateColor(s.state)}; height: 100%; width: ${(s.total / maxTotal) * 100}%;` })
      ])
    ]))
  ));
  grid.appendChild(raw);

  // Per-capita
  const pc = el('div', { class: 'card' });
  pc.appendChild(el('h2', { class: 'card-title' }, 'Per capita — kg per 1,000 residents'));
  pc.appendChild(el('p', { class: 'card-subtitle' }, 'Normalised by 2024 ABS ERP population — reveals which state dumps the most per person.'));
  const items = perCapitaByState(ctx.data.meta);
  const max = items[0]?.perCapita ?? 1;
  pc.appendChild(el('div', { style: 'display: flex; flex-direction: column; gap: var(--space-md);' },
    items.map(i => el('div', null, [
      el('div', { style: 'display: flex; justify-content: space-between; font-size: var(--font-size-sm); margin-bottom: 4px' }, [
        el('span', null, STATE_LABELS[i.state] ?? i.state),
        el('span', { class: 'text-mono' }, formatMass(i.perCapita) + ' / 1k')
      ]),
      el('div', { style: 'background: var(--bg-elevated); border-radius: 6px; height: 12px; overflow: hidden;' }, [
        el('div', { style: `background: ${stateColor(i.state)}; height: 100%; width: ${(i.perCapita / max) * 100}%;` })
      ]),
      el('div', { class: 'text-tertiary', style: 'font-size: var(--font-size-xs); margin-top: 4px' },
        `Population ${i.population.toLocaleString('en-AU')} · total ${formatMass(i.total)}`)
    ]))
  ));
  grid.appendChild(pc);

  root.appendChild(grid);

  // Heatmap: state × year totals
  const heat = el('div', { class: 'card' });
  heat.appendChild(el('h2', { class: 'card-title' }, 'State × year heatmap'));
  heat.appendChild(el('p', { class: 'card-subtitle' }, 'Annual emission intensity per state. Darker = more total release.'));

  const grouped = groupStateYear(ctx.data.stateYear);
  // Determine year range
  const years: number[] = [];
  for (let y = ctx.data.meta.earliest_year ?? 2000; y <= ctx.data.meta.latest_year; y++) years.push(y);
  // Determine max value
  let maxVal = 0;
  for (const arr of grouped.values()) {
    for (const r of arr) {
      const t = r.air + r.water + r.land;
      if (t > maxVal) maxVal = t;
    }
  }
  // Build table
  const tbl = el('table', { class: 'heatmap' });
  const head = el('tr', null, [
    el('th', { class: 'label' }, 'State'),
    ...years.map(y => el('th', null, String(y).slice(2)))
  ]);
  const thead = el('thead', null, [head]);
  tbl.appendChild(thead);
  const tbody = el('tbody');
  for (const s of STATE_ORDER) {
    const rowArr = grouped.get(s) ?? [];
    const byYear = new Map<number, number>();
    for (const r of rowArr) byYear.set(r.year, r.air + r.water + r.land);
    const cells = [el('td', { class: 'label' }, STATE_LABELS[s] ?? s)];
    for (const y of years) {
      const v = byYear.get(y) ?? 0;
      const intensity = v === 0 ? 0 : Math.min(1, Math.log10(1 + v) / Math.log10(1 + maxVal));
      const cell = el('td', {
        'data-tip': `${s} · ${formatYear(y)} · ${formatMass(v)}`,
        'aria-label': `${s} · ${formatYear(y)} · ${formatMass(v)}`,
        style: v === 0 ? '' : `background:${heatColor(intensity)}`
      }, v === 0 ? '' : '');
      if (v === 0) cell.className = 'empty';
      cells.push(cell);
    }
    tbody.appendChild(el('tr', null, cells));
  }
  tbl.appendChild(tbody);
  heat.appendChild(tbl);
  root.appendChild(heat);
}

function seg(frac: number, color: string): HTMLElement {
  return el('div', { style: `background:${color}; width: ${Math.max(0, frac * 100)}%; height: 100%;` });
}
